import React, { useEffect, useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { cn } from '../../../utils/cn';
import { authHealthMetrics } from '../authData';
import type { AuthVisualVariant } from '../authVisualVariant';

function AnimatedScore({ target, reduceMotion }: { target: number; reduceMotion: boolean | null }) {
  const [value, setValue] = useState(reduceMotion ? target : 0);

  useEffect(() => {
    if (reduceMotion) {
      setValue(target);
      return undefined;
    }
    const start = performance.now();
    const duration = 1100;
    let frame = 0;
    const tick = (now: number) => {
      const t = Math.min((now - start) / duration, 1);
      const eased = 1 - (1 - t) ** 3;
      setValue(Math.round(eased * target));
      if (t < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [target, reduceMotion]);

  return <>{value}</>;
}

function HealthRing({
  score,
  color,
  label,
  delay,
  dark,
  compact,
}: {
  score: number;
  color: string;
  label: string;
  delay: number;
  dark: boolean;
  compact?: boolean;
}) {
  const reduceMotion = useReducedMotion();
  const [hovered, setHovered] = useState(false);
  const r = compact ? 18 : 22;
  const circumference = 2 * Math.PI * r;
  const offset = circumference - (score / 100) * circumference;

  return (
    <motion.button
      type="button"
      className={cn(
        'flex flex-col items-center gap-1 rounded-lg p-1 text-left transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/40',
        dark ? 'hover:bg-white/5' : 'hover:bg-slate-50/80'
      )}
      onHoverStart={() => setHovered(true)}
      onHoverEnd={() => setHovered(false)}
      initial={reduceMotion ? false : { opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay, duration: 0.4 }}
      aria-label={`${label}: ${score} percent`}
    >
      <motion.div
        className={cn('relative', compact ? 'h-11 w-11' : 'h-14 w-14')}
        animate={hovered && !reduceMotion ? { y: -2 } : { y: 0 }}
      >
        <svg className="h-full w-full -rotate-90" viewBox="0 0 56 56" aria-hidden>
          <circle cx="28" cy="28" r={r} fill="none" stroke={dark ? '#334155' : '#f1f5f9'} strokeWidth="4" />
          <motion.circle
            cx="28"
            cy="28"
            r={r}
            fill="none"
            stroke={color}
            strokeWidth={hovered ? 4.5 : 4}
            strokeLinecap="round"
            strokeDasharray={circumference}
            initial={{ strokeDashoffset: reduceMotion ? offset : circumference }}
            animate={{ strokeDashoffset: offset }}
            transition={{ duration: reduceMotion ? 0 : 1.15, ease: 'easeOut', delay: delay * 0.5 }}
          />
        </svg>
        <span
          className={cn(
            'absolute inset-0 flex items-center justify-center text-xs font-semibold tabular-nums',
            dark ? 'text-slate-100' : 'text-slate-900'
          )}
        >
          <AnimatedScore target={score} reduceMotion={reduceMotion} />
        </span>
        {hovered && !reduceMotion ? (
          <span className="absolute inset-0 rounded-full ring-2 ring-blue-500/20" aria-hidden />
        ) : null}
      </motion.div>
      <span
        className={cn(
          'max-w-[4.5rem] text-center text-[10px] font-medium leading-tight',
          dark ? 'text-slate-400' : 'text-slate-500'
        )}
      >
        {label}
      </span>
    </motion.button>
  );
}

interface AuthKpiRingsProps {
  variant?: AuthVisualVariant;
  compact?: boolean;
}

export const AuthKpiRings: React.FC<AuthKpiRingsProps> = ({ variant = 'light', compact = false }) => {
  const dark = variant === 'dark';
  return (
  <motion.div
    className={cn(
      'grid grid-cols-4 gap-1.5 border-b',
      compact ? 'px-3 py-2.5' : 'gap-2 px-4 py-4',
      dark ? 'border-white/10' : 'border-slate-100'
    )}
    initial="hidden"
    animate="visible"
  >
    {authHealthMetrics.map((m, i) => (
      <HealthRing
        key={m.label}
        score={m.score}
        color={m.color}
        label={m.label}
        delay={0.2 + i * 0.08}
        dark={dark}
        compact={compact}
      />
    ))}
  </motion.div>
  );
};
